import type { TagItem } from '@/components/atoms/Tag/Tag.types';
import { buildProcessTree } from '@/components/organisms/HeaderFilters/filterComponents/lib/processes';
import type { OptionValue, SelectOption, TreeOption } from '@/components/organisms/Select/types';
import type {
  NewAppliedFilters,
  NewFilterGroupCounts,
  NewFilterKey,
  NewFiltersClearOptions,
  NewFiltersOptions,
  NewMultiFilterKey,
  NewVisibleFilterKey,
} from '@/context/filters.context/lib/types';
import type {
  CountryFilterOption,
  DepartmentApiNode,
  EuralApiNode,
  LocationApiOption,
  ProcessApiNode,
} from '@/lib/api/types/filters';
import { hasData, hasResults, isDefinedValue, type Primitive } from '@/lib/guards';
import { getSelectOptionLabel, getTreeOptionLabel, isLeafTreeOption } from '@/lib/selectOptions';

import {
  getFilterFields,
  isCustomFilterField,
  isSingleFilterField,
  isTreeFilterField,
  type ActiveFilterViewModel,
  type FilterGroupConfig,
  type NewFilterFieldConfig,
} from './filterConfig';

import type { FilterBarShellActiveFilterContent } from '../FilterBarShell';

export type NamedApiOption = {
  id?: Primitive | null;
  key?: Primitive | null;
  name?: string | null;
};

type CountryOption = CountryFilterOption & { disabled?: boolean };
type DropdownOptionLabels = {
  countryBenchmarkDescription?: string;
};

export type ActiveFilterTagContent = FilterBarShellActiveFilterContent;

export function normalizeApiList<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (hasResults<T>(value)) return value.results;
  if (hasData<T>(value)) return value.data;
  return [];
}

export function sortSelectOptions(options: SelectOption[]): SelectOption[] {
  return options.slice().sort((a, b) => a.label.localeCompare(b.label));
}

export function toNamedOptions(value: unknown): SelectOption[] {
  return sortSelectOptions(
    normalizeApiList<NamedApiOption>(value)
      .filter((item) => isDefinedValue(item.id) && Boolean(item.name))
      .map((item) => ({
        label: String(item.name),
        value: String(item.id),
      }))
  );
}

export function toCountryOptions(value: unknown, labels: DropdownOptionLabels = {}): SelectOption[] {
  return sortSelectOptions(
    normalizeApiList<CountryOption>(value).map((country) => ({
      description: country.has_benchmark ? labels.countryBenchmarkDescription : undefined,
      disabled: Boolean(country.disabled),
      label: country.name,
      value: String(country.id),
    }))
  );
}

export function toDisposerOptions(value: unknown): TreeOption[] {
  function mapDepartmentNodesToTreeOptions(nodes: DepartmentApiNode[], parentKey = ''): TreeOption[] {
    return nodes.map((node) => {
      const idPart = node.id ?? node.code;
      const optionValue = parentKey ? `${parentKey}-${node.code}` : String(idPart);

      return {
        label: node.code,
        value: optionValue,
        children:
          node.children?.length && isDefinedValue(idPart)
            ? mapDepartmentNodesToTreeOptions(node.children, String(idPart))
            : undefined,
      };
    });
  }

  return mapDepartmentNodesToTreeOptions(normalizeApiList<DepartmentApiNode>(value));
}

export function toEuralOptions(value: unknown): TreeOption[] {
  function walk(nodes: EuralApiNode[]): TreeOption[] {
    return nodes.map((node) => ({
      label: `${node.code} - ${node.name}`,
      value: node.code,
      children: node.children?.length ? walk(node.children) : undefined,
    }));
  }

  return walk(normalizeApiList<EuralApiNode>(value));
}

export function toLocationOptions(value: unknown): SelectOption[] {
  const seen = new Set<string>();

  return normalizeApiList<LocationApiOption>(value)
    .map((location) => {
      const postcode = String(location.user_postcode ?? '').trim();
      const city = String(location.user_city ?? '').trim();

      if (postcode && city) return `${postcode}, ${city}`;
      return postcode || city;
    })
    .filter((label) => {
      if (!label || seen.has(label)) return false;
      seen.add(label);
      return true;
    })
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ label, value: label }));
}

export function toProcessOptions(value: unknown): TreeOption[] {
  return buildProcessTree(normalizeApiList<ProcessApiNode>(value)).treeData;
}

export function buildDropdownOptions(
  defaultFilters: unknown,
  fallbackOptions: NewFiltersOptions,
  labels: DropdownOptionLabels = {}
): NewFiltersOptions {
  const filters =
    defaultFilters && typeof defaultFilters === 'object' ? (defaultFilters as Record<string, unknown>) : {};

  return {
    countries: toCountryOptions(filters?.countries, labels),
    disposers: toDisposerOptions(filters?.disposers),
    eurals: toEuralOptions(filters?.eurals),
    hazardous: fallbackOptions.hazardous,
    locations: toLocationOptions(filters?.locations),
    materials: toNamedOptions(filters?.materials),
    processes: toProcessOptions(filters?.processes),
    processors: toNamedOptions(filters?.processors),
    projects: toNamedOptions(filters?.projects),
    radius: fallbackOptions.radius,
  };
}

export function toTagItems(
  values: OptionValue[],
  getLabel: (value: OptionValue) => string,
  options: {
    disabled?: boolean;
    getTooltipTitle?: (value: OptionValue) => string | undefined;
    onRemove?: (value: OptionValue) => void;
  } = {}
): TagItem[] {
  return values.map((value) => ({
    disabled: options.disabled,
    key: value,
    label: getLabel(value),
    onRemove: options.onRemove ? () => options.onRemove?.(value) : undefined,
    tooltipTitle: options.getTooltipTitle?.(value),
  }));
}

export function isActiveFilter(filter: ActiveFilterViewModel | null): filter is ActiveFilterViewModel {
  return filter !== null;
}

function findTreeOption(options: readonly TreeOption[], value: OptionValue): TreeOption | undefined {
  for (const option of options) {
    if (option.value === value) return option;

    const nestedOption = option.children ? findTreeOption(option.children, value) : undefined;
    if (nestedOption) return nestedOption;
  }

  return undefined;
}

/**
 * Tree selects keep parent ids in state so checkbox ancestry can render
 * correctly. FiltersBar summaries represent real active criteria, so they
 * expose only selected leaves.
 */
export function getVisibleFilterValues(
  field: NewFilterFieldConfig,
  filters: NewAppliedFilters,
  options: NewFiltersOptions
): OptionValue[] {
  if (isCustomFilterField(field)) return (filters[field.key] ?? []) as OptionValue[];

  if (isSingleFilterField(field)) {
    const value = filters[field.key];

    return value == null ? [] : [value];
  }

  const selectedValues = (filters[field.key] ?? []) as OptionValue[];

  if (!isTreeFilterField(field)) return selectedValues;

  const treeOptions = options[field.optionKey] as TreeOption[];

  return selectedValues?.filter((value) => {
    const option = findTreeOption(treeOptions, value);

    return option ? isLeafTreeOption(option) : true;
  });
}

export function getFilterCount(
  field: NewFilterFieldConfig,
  filters: NewAppliedFilters,
  options: NewFiltersOptions
): number {
  return getVisibleFilterValues(field, filters, options).length;
}

export function isReadonlyFilterField(field: NewFilterFieldConfig, options: NewFiltersOptions): boolean {
  return field.disabled === true || (field.key === 'countries' && options.countries.length === 1);
}

/**
 * Builds reducer clear options from current availability. A single available
 * country is a required business constraint and must survive clear/reset.
 */
export function getClearFiltersOptions(options: NewFiltersOptions): NewFiltersClearOptions | undefined {
  const [country] = options.countries;

  return options.countries.length === 1 && country ? { preservedCountries: [country.value] } : undefined;
}

export function countFilterGroupsForView(
  filters: NewAppliedFilters,
  options: NewFiltersOptions,
  filterGroups: readonly FilterGroupConfig[]
): NewFilterGroupCounts {
  return filterGroups.reduce<NewFilterGroupCounts>(
    (counts, group) => ({
      ...counts,
      [group.key]: group.fields.reduce((total, field) => {
        return total + getFilterCount(field, filters, options);
      }, 0),
    }),
    {
      businessUnit: 0,
      general: 0,
      organization: 0,
      origin: 0,
      processing: 0,
      waste: 0,
    }
  );
}

/**
 * Counts filters the user can clear from the FiltersBar. This is option-aware
 * because a single available country is selected by default and readonly.
 */
export function countFilters(
  filters: NewAppliedFilters,
  options: NewFiltersOptions,
  filterGroups: readonly FilterGroupConfig[]
): number {
  return filterGroups.reduce(
    (total, group) =>
      total +
      group.fields.reduce((groupTotal, field) => {
        if (isReadonlyFilterField(field, options)) return groupTotal;

        return groupTotal + getFilterCount(field, filters, options);
      }, 0),
    0
  );
}

function countRawFilterValue(value: unknown): number {
  if (Array.isArray(value)) return value.length;

  return value == null ? 0 : 1;
}

export function countInactiveFilters(
  filters: NewAppliedFilters,
  options: NewFiltersOptions,
  supportedKeys: readonly NewVisibleFilterKey[],
  allFilterGroups: readonly FilterGroupConfig[]
): number {
  const supportedKeySet = new Set<NewVisibleFilterKey>(supportedKeys);
  const configuredFields = getFilterFields(allFilterGroups);
  const configuredFieldKeys = new Set<NewVisibleFilterKey>(configuredFields.map((field) => field.key));

  const configuredCount = configuredFields.reduce((total, field) => {
    if (supportedKeySet.has(field.key) || isReadonlyFilterField(field, options)) return total;

    return total + getFilterCount(field, filters, options);
  }, 0);

  const unconfiguredCount = Object.entries(filters).reduce((total, [key, value]) => {
    const filterKey = key as NewVisibleFilterKey;

    if (key === 'period' || supportedKeySet.has(filterKey) || configuredFieldKeys.has(filterKey)) return total;

    return total + countRawFilterValue(value);
  }, 0);

  return configuredCount + unconfiguredCount;
}

export function getActiveFilterTagContent(filter: ActiveFilterViewModel): ActiveFilterTagContent {
  const [singleItem] = filter.items;

  if (singleItem && filter.items.length === 1) {
    return {
      items: [],
      label: singleItem.label,
    };
  }

  return {
    items: filter.items,
    label: `${filter.count} ${filter.label}`,
  };
}

export function getFieldOptionLabel(
  field: NewFilterFieldConfig,
  options: NewFiltersOptions,
  value: OptionValue
): string {
  if (isCustomFilterField(field)) return getSelectOptionLabel(field.options, value);
  if (isTreeFilterField(field)) return getTreeOptionLabel(options[field.optionKey] as TreeOption[], value);

  return getSelectOptionLabel(options[field.optionKey] as SelectOption[], value);
}

function getFieldOptionTooltipTitle(
  field: NewFilterFieldConfig,
  options: NewFiltersOptions,
  value: OptionValue
): string | undefined {
  if (isCustomFilterField(field)) return undefined;
  if (field.key !== 'eurals' || !isTreeFilterField(field)) return undefined;

  return findTreeOption(options[field.optionKey] as TreeOption[], value)?.label;
}

export function buildActiveFilter(params: {
  clearAppliedFilter: (filter: NewFilterKey) => void;
  field: NewFilterFieldConfig;
  filters: NewAppliedFilters;
  getLabel: (key: string, options: { count: number }) => string;
  options: NewFiltersOptions;
  removeAppliedFilterValue: (filter: NewMultiFilterKey, value: OptionValue) => void;
}): ActiveFilterViewModel | null {
  const { clearAppliedFilter, field, filters, getLabel, options, removeAppliedFilterValue } = params;
  const visibleValues = getVisibleFilterValues(field, filters, options);
  const count = visibleValues.length;
  const isReadonly = isReadonlyFilterField(field, options);

  if (count === 0) return null;

  if (isSingleFilterField(field)) {
    const [value] = visibleValues;

    if (!value) return null;

    return {
      clearable: !isReadonly,
      count,
      disabled: isReadonly,
      icon: field.icon,
      items: [
        {
          key: value,
          label: getFieldOptionLabel(field, options, value),
          onRemove: isReadonly ? undefined : () => clearAppliedFilter(field.key),
        },
      ],
      key: field.key,
      label: getLabel(field.labelKey, { count }),
    };
  }

  const label = isCustomFilterField(field) ? field.label : getLabel(field.labelKey, { count });

  return {
    clearable: !isReadonly,
    count,
    disabled: isReadonly,
    icon: field.icon,
    items: toTagItems(visibleValues, (value) => getFieldOptionLabel(field, options, value), {
      disabled: isReadonly,
      getTooltipTitle: (value) => getFieldOptionTooltipTitle(field, options, value),
      onRemove: isReadonly ? undefined : (value) => removeAppliedFilterValue(field.key, value),
    }),
    key: field.key,
    label,
  };
}
